import { forkJoin, map, Observable, of } from 'rxjs';

import { CelumPropertiesProvider } from '@celum/core/configuration';
import { appendUrlSearchParam, hasValue } from '@celum/core/util';

import { ApiConfiguration, AuthInterceptorConfig, ServiceTokenInterceptorConfig, TokenEndpointConfiguration } from '../auth.interceptor';
import { isB2CRequest, NamedAuthToken, RequestOptionsForMultipleTokens, RequestOptionsForSingleToken } from '../model/auth-token.model';
import { ServiceAccessTokenProvider } from '../services/service-access-token-provider.service';

/**
 * Get the authentication tokens which should be used for the service the passed url is pointing to.
 * Returns `null` in case there is no configuration available.
 *
 * @param url                           the url to get the tokens for
 * @param interceptorConfig             the interceptor configs for the "supported" services
 * @param tokenProvider                 the service which retrieves tokens from endpoints
 * @param opts                          additional options, e.g. tokenLeadTimeInMilliseconds - the duration in milliseconds before token expiration before it is refreshed
 */
export function getAuthTokens(
  url: string,
  interceptorConfig: ServiceTokenInterceptorConfig<AuthInterceptorConfig>,
  tokenProvider: ServiceAccessTokenProvider,
  opts?: RequestOptionsForMultipleTokens
): Observable<NamedAuthToken[]> {
  const configs = retrieveApiConfig(interceptorConfig, url)?.tokenEndpointConfigs;

  if (!hasValue(configs)) {
    return of(null);
  }

  // get all necessary tokens for the given url
  const tokenRequests$ = configs.map(endpoint => {
    const optionsForSingleToken = getOptionsForSingleToken(endpoint, opts);

    return tokenProvider
      .getServiceAccessToken(endpoint.serviceTokenRequestDto, optionsForSingleToken)
      .pipe(map(token => ({ token, name: endpoint.tokenQueryParamName ?? 'token' })));
  });

  return forkJoin(tokenRequests$).pipe(map(tokens => tokens.filter(token => hasValue(token?.token))));
}

export function hasAuthToken(
  url: string,
  interceptorConfig: ServiceTokenInterceptorConfig<AuthInterceptorConfig>,
  tokenProvider: ServiceAccessTokenProvider,
  tokenName?: string,
  opts?: RequestOptionsForMultipleTokens
): boolean {
  const configs = retrieveApiConfig(interceptorConfig, url)?.tokenEndpointConfigs;

  if (!hasValue(configs)) {
    return false;
  }

  // get all necessary tokens for the given url
  const endPoint = configs.find(endpoint => (endpoint.tokenQueryParamName ?? 'token') === tokenName);

  if (!endPoint) {
    return false;
  }

  const optionsForSingleToken = getOptionsForSingleToken(endPoint, opts);

  return tokenProvider.hasServiceAccessToken(endPoint.serviceTokenRequestDto, optionsForSingleToken);
}

export function appendTokensToUrl(url: string, tokens: NamedAuthToken[], replace = true): string {
  if (!url || !tokens) {
    return url;
  }

  return tokens.reduce((acc, token) => appendTokenToUrl(acc, token, { replace }), url);
}

export type AppendTokenOptions = { replace?: boolean };

export function appendTokenToUrl(url: string, token: NamedAuthToken, options?: AppendTokenOptions): string {
  const replace = options?.replace ?? true;
  const tokenName = token?.name ?? 'token';

  if (!url) {
    return url;
  }

  // null means no token could be obtained. Undefined is currently reserved for unprotected portals, which do not require tokens.
  if (token === null) {
    console.debug(`AuthTokenUtil: no token returned for url '${url}'.`);
  }

  if (!token) {
    return url;
  }

  return appendUrlSearchParam(url, tokenName, token.token, replace);
}

export function retrieveApiConfig(interceptorConfig: ServiceTokenInterceptorConfig<AuthInterceptorConfig>, url: string): ApiConfiguration {
  const apiConfigurations = interceptorConfig?.getInterceptorConfiguration().apiConfigurations;
  return apiConfigurations?.find(config => config.apiUrls.some(apiUrl => url.startsWith(apiUrl)));
}

export function retrieveTokenEndpoints(config: ServiceTokenInterceptorConfig<AuthInterceptorConfig>): string[] {
  return [
    ...new Set(
      config
        .getInterceptorConfiguration()
        .apiConfigurations.flatMap(apiConfig => apiConfig.tokenEndpointConfigs)
        .map(tokenConfig => tokenConfig.tokenEndpoint)
        .filter(Boolean)
    )
  ];
}

/**
 * Checks if the given request requires a B2C token.
 *
 * @param endpointConfigs The endpoint configurations for the given url
 * @param url The URL to check
 */
export function isB2COrSaccRequest(endpointConfigs: TokenEndpointConfiguration[], url: string): boolean {
  return hasValue(getB2CConfigs(endpointConfigs, url)) || url.includes(CelumPropertiesProvider.properties.authentication.saccUrl);
}

export function getB2CConfigs(endpointConfigs: TokenEndpointConfiguration[], url: string): TokenEndpointConfiguration[] {
  return endpointConfigs?.filter(config => isB2CRequest(config?.serviceTokenRequestDto));
}

/**
 * Returns a sample url which is representative for the given api config id. It can be used to fetch tokens for the given api config id.
 *
 * @param interceptorConfig - the interceptor config
 * @param apiConfigId - the api config id
 */
export function getSampleUrlForConfigId(interceptorConfig: ServiceTokenInterceptorConfig<AuthInterceptorConfig>, apiConfigId: string): string {
  const apiConfiguration = interceptorConfig.getInterceptorConfiguration().apiConfigurations.find(apiConfig => apiConfig.id === apiConfigId);
  return apiConfiguration?.apiUrls?.[0];
}

/**
 * Gets the request options for fetching a token from a specific endpoint.
 *
 * @param endpoint the endpoint for which the request options should be constructed
 * @param opts the request options for multiple endpoints
 */
export function getOptionsForSingleToken(endpoint: TokenEndpointConfiguration, opts?: RequestOptionsForMultipleTokens): RequestOptionsForSingleToken {
  return {
    tokenLeadTimeInMilliseconds: opts?.tokenLeadTimeInMilliseconds,
    endpoint: endpoint.tokenEndpoint,
    context: opts?.endpointsWithContexts?.[endpoint.tokenEndpoint] ?? {},
    noRefetch: endpoint.noRefetch || opts?.noRefetch,
    tokenProperty: endpoint.tokenProperty
  };
}
